import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton
} from '@mui/material';
import {
  Download as ImportIcon,
  Category as CategoryIcon,
  CheckCircle as ProcessedIcon,
  Schedule as PendingIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

// Types
interface BankTransaction {
  id: string;
  account_id: string;
  transaction_date: string;
  description: string;
  amount: number;
  amount_formatted: string;
  transaction_type: 'DEBIT' | 'CREDIT';
  balance: number;
  balance_formatted: string;
  reference: string;
  category_id?: number;
  category_name?: string;
  status: 'PENDING' | 'PROCESSED' | 'IGNORED';
  merchant_name?: string;
  suggested_category?: string;
}

interface TransactionImportProps {
  bankAccounts: any[];
  onRefresh: () => void;
  loading: boolean;
}

const TransactionImport: React.FC<TransactionImportProps> = ({
  bankAccounts,
  onRefresh,
  loading
}) => {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [categorizing, setCategorizing] = useState(false);

  // Mock expense categories for demonstration
  const expenseCategories = [
    { id: 1, name: 'Travel & Transport' },
    { id: 2, name: 'Meals & Entertainment' },
    { id: 3, name: 'Office Supplies' },
    { id: 4, name: 'Professional Services' },
    { id: 5, name: 'Marketing & Advertising' },
    { id: 6, name: 'Utilities' },
    { id: 7, name: 'Insurance' },
    { id: 8, name: 'Bank Charges' }
  ];

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
    }
  }, [selectedAccount, statusFilter]);

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedAccount) params.append('account_id', selectedAccount);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`/api/v1/banking/transactions?${params}`);
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleImportTransactions = async () => {
    if (!selectedAccount) return;
    
    try {
      setImporting(true);
      
      const response = await fetch(`/api/v1/banking/accounts/${selectedAccount}/import`, {
        method: 'POST'
      });

      if (response.ok) {
        fetchTransactions();
      }
    } catch (error) {
      console.error('Error importing transactions:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleBulkCategorize = async () => {
    if (selectedTransactions.length === 0) return;
    
    try {
      setCategorizing(true);
      
      const response = await fetch('/api/v1/banking/transactions/bulk-categorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction_ids: selectedTransactions })
      });

      if (response.ok) {
        fetchTransactions();
        setSelectedTransactions([]);
      }
    } catch (error) {
      console.error('Error categorizing transactions:', error);
    } finally {
      setCategorizing(false);
    }
  };

  const handleTransactionSelect = (transactionId: string) => {
    setSelectedTransactions(prev => 
      prev.includes(transactionId)
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSED': return 'success';
      case 'PENDING': return 'warning';
      case 'IGNORED': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PROCESSED': return <ProcessedIcon />;
      case 'PENDING': return <PendingIcon />;
      case 'IGNORED': return <ErrorIcon />;
      default: return <PendingIcon />;
    }
  };

  const getTransactionTypeColor = (type: string, amount: number) => {
    if (type === 'CREDIT' || amount > 0) return 'success';
    return 'error';
  };

  // Mock transaction data for demonstration
  const mockTransactions: BankTransaction[] = [
    {
      id: '1',
      account_id: 'acc1',
      transaction_date: '2024-09-14',
      description: 'TESCO STORES 3297 LONDON',
      amount: -45.67,
      amount_formatted: '-£45.67',
      transaction_type: 'DEBIT',
      balance: 2543.21,
      balance_formatted: '£2,543.21',
      reference: 'DEB-20240914-001',
      status: 'PENDING',
      merchant_name: 'Tesco',
      suggested_category: 'Office Supplies'
    },
    {
      id: '2',
      account_id: 'acc1',
      transaction_date: '2024-09-13',
      description: 'SALARY PAYMENT - ACME CORP',
      amount: 3500.00,
      amount_formatted: '£3,500.00',
      transaction_type: 'CREDIT',
      balance: 2588.88,
      balance_formatted: '£2,588.88',
      reference: 'SAL-20240913-001',
      status: 'PROCESSED',
      category_name: 'Income'
    },
    {
      id: '3',
      account_id: 'acc1',
      transaction_date: '2024-09-12',
      description: 'UBER TRIP LONDON',
      amount: -18.50,
      amount_formatted: '-£18.50',
      transaction_type: 'DEBIT',
      balance: -911.12,
      balance_formatted: '-£911.12',
      reference: 'DEB-20240912-001',
      status: 'PENDING',
      merchant_name: 'Uber',
      suggested_category: 'Travel & Transport'
    }
  ];

  const displayTransactions = transactions.length > 0 ? transactions : mockTransactions;

  return (
    <Box>
      {/* Import Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Transaction Import
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Bank Account</InputLabel>
                <Select
                  value={selectedAccount}
                  label="Bank Account"
                  onChange={(e) => setSelectedAccount(e.target.value)}
                >
                  {bankAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.bank_name} - {account.account_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="PROCESSED">Processed</MenuItem>
                  <MenuItem value="IGNORED">Ignored</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<ImportIcon />}
                onClick={handleImportTransactions}
                disabled={!selectedAccount || importing}
                fullWidth
              >
                {importing ? 'Importing...' : 'Import New Transactions'}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<CategoryIcon />}
                onClick={handleBulkCategorize}
                disabled={selectedTransactions.length === 0 || categorizing}
                fullWidth
              >
                {categorizing ? 'Categorizing...' : 'Auto-Categorize'}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="textSecondary">
                {displayTransactions.length} transaction{displayTransactions.length !== 1 ? 's' : ''}
                {selectedTransactions.length > 0 && ` (${selectedTransactions.length} selected)`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Import Status */}
      {selectedAccount && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Auto-Import Enabled:</strong> New transactions are automatically imported daily from your connected bank account.
            Last import: Today at 09:00 AM
          </Typography>
        </Alert>
      )}

      {/* Transactions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < displayTransactions.length}
                  checked={displayTransactions.length > 0 && selectedTransactions.length === displayTransactions.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTransactions(displayTransactions.map(t => t.id));
                    } else {
                      setSelectedTransactions([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Merchant</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayTransactions.map((transaction) => (
              <TableRow key={transaction.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTransactions.includes(transaction.id)}
                    onChange={() => handleTransactionSelect(transaction.id)}
                  />
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {new Date(transaction.transaction_date).toLocaleDateString('en-GB')}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {transaction.description}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {transaction.reference}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {transaction.merchant_name || '-'}
                  </Typography>
                </TableCell>
                
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    color={getTransactionTypeColor(transaction.transaction_type, transaction.amount)}
                  >
                    {transaction.amount_formatted}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Balance: {transaction.balance_formatted}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  {transaction.category_name ? (
                    <Chip label={transaction.category_name} size="small" color="primary" />
                  ) : transaction.suggested_category ? (
                    <Chip 
                      label={`Suggested: ${transaction.suggested_category}`} 
                      size="small" 
                      color="info" 
                      variant="outlined" 
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Uncategorized
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell>
                  <Chip
                    icon={getStatusIcon(transaction.status)}
                    label={transaction.status}
                    color={getStatusColor(transaction.status) as any}
                    size="small"
                  />
                </TableCell>
                
                <TableCell align="center">
                  <IconButton size="small">
                    <ViewIcon />
                  </IconButton>
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Processing Summary */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Processing Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Transactions</Typography>
                <Typography variant="h6">{displayTransactions.length}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" color="textSecondary">Processed</Typography>
                <Typography variant="h6" color="success.main">
                  {displayTransactions.filter(t => t.status === 'PROCESSED').length}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" color="textSecondary">Pending</Typography>
                <Typography variant="h6" color="warning.main">
                  {displayTransactions.filter(t => t.status === 'PENDING').length}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" color="textSecondary">Auto-Categorized</Typography>
                <Typography variant="h6" color="info.main">
                  {displayTransactions.filter(t => t.suggested_category).length}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TransactionImport;
